<script>
    import * as Utils from '@js/modules/utils';
    import I18n from '@js/modules/translations';
    import Computation from '@js/modules/computation';

    import {searchStore, SEARCH_ORDERS} from '@js/stores/searchStore';
    import {userStore} from '@js/stores/userStore';

    import SearchFilterItem from '@js/components/search/filter/SearchFilterItem.svelte';

    const {
        allFilters,
        onFilterUpdate,
        onOrderUpdate
    } = $props();

    // Slider steps: whole kilometres, five minutes
    const RANGE_STEPS = {distance: 1000, duration: 300};

    const allSelections = () => ({
        rideTypes: new Set(allFilters.rideTypes),
        levels: new Set(allFilters.levels)
    });

    const _updateFilters = Utils.debounce(() => {
        onFilterUpdate(searchStore.selectedFilters);
    }, 400);

    const onFilterChange = (filterType, filterName, filterData, checked) => {
        if (filterType === 'radio') {
            onOrderUpdate(filterData);

            return;
        }

        const selectedFilters = {...searchStore.selectedFilters};

        if (filterType === 'range') {
            const bounds = searchStore.filterBounds?.[filterName];
            const isFullRange = bounds && filterData[0] <= bounds.min && filterData[1] >= bounds.max;

            selectedFilters[filterName] = isFullRange ? undefined : new Set(filterData);
        } else if (filterType === 'select') {
            if (!selectedFilters[filterName]) {
                selectedFilters[filterName] = new Set(allSelections()[filterName]);
            }

            if (checked) {
                selectedFilters[filterName].add(filterData.id);
            } else {
                selectedFilters[filterName].delete(filterData.id);
            }

            if (selectedFilters[filterName].size === allSelections()[filterName].size) {
                selectedFilters[filterName] = undefined;
            }
        }

        searchStore.selectedFilters = selectedFilters;

        _updateFilters();
    };

    const toggleMine = () => {
        searchStore.selectedFilters = {...searchStore.selectedFilters, mine: searchStore.selectedFilters?.mine ? undefined : true};

        _updateFilters();
    };

    const clearFilters = () => {
        searchStore.selectedFilters = {};

        onFilterUpdate({});
    };

    const isActive = (filterName) => !!$searchStore.selectedFilters?.[filterName];

    const hasActiveFilters = $derived(Object.values($searchStore.selectedFilters || {}).some(Boolean));

    const formatDistance = (value) => Computation.formatDistance(value, {
        smartUnit: true,
        kUnit: false,
        decimalLimit: 0
    });

    const formatDuration = (value) => Computation.formatDuration(value, {withSeconds: false});

    const rangeOf = (filterName, bounds) => {
        if (!bounds || bounds.max <= bounds.min) {
            return null;
        }

        return [bounds.min, bounds.max];
    };

    const selectionOf = (filterName, bounds) => $searchStore.selectedFilters?.[filterName] || new Set([bounds.min, bounds.max]);

    const filterEntries = (all, available, translations) => all.map((id) => ({
        id,
        name: translations[id],
        count: available?.[id] || 0
    }));
</script>

{#if Utils.isPresent($searchStore.query)}
    <div class="flex items-center flex-wrap justify-start gap-1.5">
        <SearchFilterItem type="select"
                          name={I18n.t('js.search.index.filters.ride_type')}
                          elementId="rideTypes"
                          active={isActive('rideTypes')}
                          elements={filterEntries(allFilters.rideTypes, $searchStore.availableFilters.ride_type, I18n.t('js.ride.enums.ride_type'))}
                          selectedElements={$searchStore.selectedFilters?.rideTypes || allSelections().rideTypes}
                          onFilterChange={onFilterChange}/>

        {#each ['distance', 'duration'] as rangeName (rangeName)}
            {@const bounds = $searchStore.filterBounds?.[rangeName]}
            {@const range = rangeOf(rangeName, bounds)}
            {#if range}
                {#key range.join('-')}
                    <SearchFilterItem type="range"
                                      name={I18n.t(`js.search.index.filters.${rangeName}`)}
                                      elementId={rangeName}
                                      active={isActive(rangeName)}
                                      elements={range}
                                      step={RANGE_STEPS[rangeName]}
                                      elementFormatter={rangeName === 'distance' ? formatDistance : formatDuration}
                                      selectedElements={selectionOf(rangeName, bounds)}
                                      onFilterChange={onFilterChange}/>
                {/key}
            {/if}
        {/each}

        <SearchFilterItem type="select"
                          name={I18n.t('js.search.index.filters.level')}
                          elementId="levels"
                          active={isActive('levels')}
                          elements={filterEntries(allFilters.levels, $searchStore.availableFilters.level, I18n.t('js.ride.enums.level'))}
                          selectedElements={$searchStore.selectedFilters?.levels || allSelections().levels}
                          onFilterChange={onFilterChange}/>

        {#if $userStore.isConnected}
            <!-- Full class strings: Tailwind only generates the classes it finds written whole -->
            <button type="button"
                    class={$searchStore.selectedFilters?.mine
                        ? 'inline-flex items-center px-3 py-1 text-xs font-semibold rounded-full border cursor-pointer focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-300 text-primary-800 bg-primary-50 border-primary-300 hover:bg-primary-100'
                        : 'inline-flex items-center px-3 py-1 text-xs font-medium rounded-full border cursor-pointer focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-300 text-gray-600 bg-white border-gray-200 hover:bg-gray-50'}
                    aria-pressed={!!$searchStore.selectedFilters?.mine}
                    onclick={toggleMine}>
                {I18n.t('js.search.index.more_filters.mine')}
            </button>
        {/if}

        <SearchFilterItem type="radio"
                          name={I18n.t('js.search.index.more_filters.sort_label', {order: I18n.t(`js.search.index.sort.${$searchStore.order}`)})}
                          elementId="order"
                          active={$searchStore.order !== 'relevance'}
                          elements={SEARCH_ORDERS.map((order) => ({id: order, name: I18n.t(`js.search.index.sort.${order}`)}))}
                          selectedElements={$searchStore.order}
                          onFilterChange={onFilterChange}/>

        {#if hasActiveFilters}
            <button type="button"
                    class="px-2 py-1 text-xs font-medium text-primary-800 underline underline-offset-2 hover:text-primary-900 cursor-pointer"
                    onclick={clearFilters}>
                {I18n.t('js.search.index.more_filters.clear')}
            </button>
        {/if}
    </div>
{/if}
